import { useEffect, useState } from 'react';
import InputMask from 'react-input-mask-format';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';

import ErrorMessage from 'components/Inputs/components/ErrorMessage';
import ErrorIcon from 'components/Inputs/components/ErrorIcon';
import Label from 'components/Inputs/components/Label';

import {
  idNumberMasks,
  idNumberMaxLength,
  idNumberPatterns,
  idNumberPlaceholders,
  normalizeIdNumber,
  validateIdNumber,
} from '../utils';

const IdNumberInput = ({ control, error, typeCode, readOnly = false }) => {
  const { t } = useTranslation('dashboard');
  const [lengthError, setLengthError] = useState('');

  const mask = idNumberMasks[typeCode] || '';
  const fieldError =
    error || (lengthError ? { message: lengthError } : undefined);

  useEffect(() => {
    setLengthError('');
  }, [typeCode]);

  return (
    <div className="w-1/2">
      <Label>{t('dashboard:fields.idNumber.label')}</Label>

      <div className="relative flex">
        <Controller
          name="idNumber"
          control={control}
          render={({ field: { value, onChange } }) => {
            const maxLength = idNumberMaxLength[typeCode] || undefined;
            const pattern = idNumberPatterns[typeCode] || undefined;

            const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
              const errorKey = validateIdNumber(
                typeCode,
                e.currentTarget.value,
              );
              setLengthError(errorKey ? t(errorKey) : '');
            };

            if (!mask) {
              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = normalizeIdNumber(typeCode, e.target.value);
                if (pattern && !pattern.test(newValue)) return;
                if (maxLength && newValue.length > maxLength) return;
                onChange(newValue);
              };

              return (
                <input
                  placeholder={idNumberPlaceholders[typeCode] ?? ''}
                  value={value ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  readOnly={readOnly}
                  maxLength={maxLength}
                  className={classNames(
                    'w-full shadow-sm border focus-visible:outline-none border-gray-300 py-[7px] px-3 placeholder-gray-400 rounded-md',
                    {
                      'bg-gray-100 cursor-default focus:border-gray-300':
                        readOnly,
                    },
                    fieldError
                      ? 'pr-9 border-red-300 focus:ring-red-500 focus:border-red-500 text-red-900'
                      : 'border-gray-300 focus:ring-0 focus:border-indigo-500',
                  )}
                />
              );
            }

            return (
              <InputMask
                mask={mask}
                maskPlaceholder={null}
                placeholder={idNumberPlaceholders[typeCode] ?? ''}
                value={value ?? ''}
                onChange={onChange}
                onBlur={handleBlur}
                readOnly={readOnly}
                className={classNames(
                  'w-full shadow-sm border focus-visible:outline-none border-gray-300 py-[7px] px-3 placeholder-gray-400 rounded-md',
                  {
                    'bg-gray-100 cursor-default focus:border-gray-300':
                      readOnly,
                  },
                  fieldError
                    ? 'pr-9 border-red-300 focus:ring-red-500 focus:border-red-500 text-red-900'
                    : 'border-gray-300 focus:ring-0 focus:border-indigo-500',
                )}
              />
            );
          }}
        />

        {fieldError && <ErrorIcon />}
      </div>
      <ErrorMessage error={fieldError} />
    </div>
  );
};

export default IdNumberInput;
